import { Controller } from "@hotwired/stimulus"
import TomSelect from "tom-select"
import { debounce } from "../application"
import ProductFilterIngredientSelector from "../src/product_filter_ingredient_selector"

export default class extends Controller {
    static values = {
        fullPageSubmit: { type: Boolean, default: false },
        productsPath: String
    }

    static targets = [
        "form", "countrySelect", "vendorSelect", "ingredientSelect",
        "excludedIngredientSelect", "hiddenBrandInput",
        "showProductsCount", "showProductsLoading", "showProductsButton",
        "priceMinInput", "priceMaxInput", "nativeAction", "nativeClear",
        "appliedChips"
    ]

    initialize() {
        this.boundHandlePriceRangeChange = this.handlePriceRangeChange.bind(this)
        this.boundHandleTurboFrameLoad = this.handleTurboFrameLoad.bind(this)
    }

    connect() {
        if (!this.hasFormTarget) return

        // Create a debounced version of submitForm first, before any event handlers are attached
        const debouncedSubmitForm = debounce(() => {
            this.submitFormImpl()
        }, 100)
        this.debouncedSubmitForm = (showNativeClear = true) => {
            this.updateNativeClearVisibility(showNativeClear)
            this.refreshAppliedFilters()
            debouncedSubmitForm()
        }

        this.initializeFilters()
        this.addTurboFrameLoadListener()
        this.addPriceRangeChangeListener()
        // Deferred so the price-range controller connects first and the price
        // chip reads its corrected slider labels rather than the server defaults.
        requestAnimationFrame(() => this.refreshAppliedFilters())
    }

    initializeFilters() {
        this.setupIngredientSelectors()
        this.attachEventListeners()
    }

    // Filter input collections
    get filterInputCollections() {
        const inputElements = this.formTarget.getElementsByTagName("input")
        return {
            countryInputs: this.filterInputsByClassName(inputElements, "country-input"),
            categoryInputs: this.filterInputsByClassName(inputElements, "category-input"),
            goodForInputs: this.filterInputsByClassName(inputElements, "good-for-input"),
            ingredientCountInputs: this.filterInputsByClassName(inputElements, "ingredient-count-input"),
            spfLevelInputs: this.filterInputsByClassName(inputElements, "spf-level-input"),
            uvFilterInputs: this.filterInputsByClassName(inputElements, "uv-filter-input")
        }
    }

    attachEventListeners() {
        this.attachInputChangeListeners()
        this.attachSelectChangeListeners()
        this.attachButtonListeners()
        this.attachRadioButtonListeners()
    }

    attachInputChangeListeners() {
        const inputElements = this.formTarget.getElementsByTagName("input")
        const filterCollections = this.filterInputCollections

        Array.from(inputElements).forEach((inputElement) => {
            inputElement.addEventListener("change", (event) =>
                this.handleInputChange(event, filterCollections)
            )
        })
    }

    attachSelectChangeListeners() {
        if (this.hasCountrySelectTarget) {
            this.countrySelectTarget.addEventListener("change", () => this.handleCountrySelectChange())
        }
        if (this.hasVendorSelectTarget) {
            this.vendorSelectTarget.addEventListener("change", () => this.debouncedSubmitForm())
        }
        if (this.hasIngredientSelectTarget) {
            this.ingredientSelectTarget.addEventListener("change", () => this.debouncedSubmitForm())
        }
        if (this.hasExcludedIngredientSelectTarget) {
            this.excludedIngredientSelectTarget.addEventListener("change", () => this.debouncedSubmitForm())
        }
    }

    addPriceRangeChangeListener() {
        document.addEventListener('price-range:change', this.boundHandlePriceRangeChange)
    }

    handlePriceRangeChange(event) {
        if (!this.hasPriceMinInputTarget || !this.hasPriceMaxInputTarget) return

        // Find the min and max price inputs in the form
        const minInput = this.priceMinInputTarget
        const maxInput = this.priceMaxInputTarget

        // If inputs exist, update their values
        if (minInput) {
            minInput.value = event.detail.minValue
        }

        if (maxInput) {
            maxInput.value = event.detail.maxValue
        }

        if (minInput.value === "" || minInput.value === "0" || minInput.value === 0) {
            console.log("minInput is disabled")
            minInput.disabled = true
        } else {
            console.log("minInput is enabled")
            minInput.disabled = false
        }

        if (maxInput.value === "" || maxInput.value === "100" || maxInput.value === 100) {
            console.log("maxInput is disabled")
            maxInput.disabled = true
        } else {
            console.log("maxInput is enabled")
            maxInput.disabled = false
        }

        // Submit the form with updated values
        this.debouncedSubmitForm()
    }

    handleCountrySelectChange() {
        this.syncCountryControls()
        this.clearBrandFilter()
        this.debouncedSubmitForm()
    }

    // The quick-pick checkboxes and the dropdown post to the same
    // `product_filter[country][]` name, so a code held by both would be sent
    // twice. The checkboxes win: a dropdown pick that has a quick pick moves to
    // it, leaving the dropdown holding only the countries without one.
    syncCountryControls() {
        if (!this.hasCountrySelectTarget) return

        const quickPicks = this.quickCountryPicks()

        Array.from(this.countrySelectTarget.selectedOptions).forEach(option => {
            const checkbox = quickPicks.get(option.value)
            if (!checkbox) return

            checkbox.checked = true
            this.deselectCountryOption(option.value)
        })
    }

    quickCountryPicks() {
        return new Map(this.filterInputCollections.countryInputs.map(input => [input.value, input]))
    }

    // Go through TomSelect when it is present so its visible items stay in step
    // with the underlying select, and silently so the sync does not re-enter the
    // change handler that called it.
    deselectCountryOption(code) {
        const select = this.countrySelectTarget
        if (select.tomselect) return select.tomselect.removeItem(code, true)

        Array.from(select.options).forEach(option => {
            if (option.value === code) option.selected = false
        })
    }

    clearBrandFilter() {
        if (this.hasHiddenBrandInputTarget) this.hiddenBrandInputTarget.value = ""
    }

    attachButtonListeners() {
        this.addClearFiltersButtonListener()
        this.addAppClearFiltersButtonListener()
        this.addRemoveBrandFilterListener()

        // Add an event listener once using a data attribute to track if it's already attached
        this.showProductsButtonTargets.forEach(button => {
            if (!button.dataset.hasClickListener) {
                button.addEventListener('click', this.handleShowProductsClick.bind(this))
                button.dataset.hasClickListener = 'true'
            }
        })
    }

    attachRadioButtonListeners() {
        // For radio buttons other than country inputs
        this.formTarget.querySelectorAll('input[type="radio"]:not([name="product_filter[order_by]"]):not(.ingredient-match-input)').forEach(radio => {
            // Initialize the lastChecked data attribute
            radio.dataset.lastChecked = radio.checked ? 'true' : 'false'
            radio.addEventListener('click', this.handleRadioClick.bind(this))
        })
    }

    handleRadioClick(event) {
        if (event.target.name === 'product_filter[order_by]') return

        // For category inputs, we need to ensure they stay checked
        // and don't get unchecked when clicked again
        if (event.target.classList.contains('category-input')) {
            this.handleCategoryRadioClick(event)
            return
        }

        // For other radio buttons, allow toggling behavior
        this.handleToggleableRadioClick(event)
    }

    handleCategoryRadioClick(event) {
        this.formTarget.querySelectorAll(`input[name="${event.target.name}"]`).forEach(r => {
            r.checked = r === event.target
            r.dataset.lastChecked = r === event.target ? 'true' : 'false'
        })
        this.debouncedSubmitForm()
    }

    handleToggleableRadioClick(event) {
        // If the clicked button is already checked, uncheck it (toggle off)
        if (event.target.checked && event.target.dataset.lastChecked === 'true') {
            event.target.checked = false
            event.target.dataset.lastChecked = 'false'
        }
        // If a different button is clicked, select it and unselect others
        else {
            // Uncheck all other radio buttons in the same group
            this.formTarget.querySelectorAll(`input[name="${event.target.name}"]`).forEach(r => {
                if (r !== event.target) {
                    r.checked = false
                    r.dataset.lastChecked = 'false'
                }
            })

            // Check the clicked button
            event.target.checked = true
            event.target.dataset.lastChecked = 'true'
        }

        this.debouncedSubmitForm()
    }

    addClearFiltersButtonListener() {
        const clearButtons = document.querySelectorAll(".clear-filters-button")
        clearButtons.forEach((button) => {
            button.addEventListener("click", (event) => this.handleClearFiltersButtonClick(event))
        })
    }

    addAppClearFiltersButtonListener() {
        const clearButtons = document.querySelectorAll(".app-clear-filters-button")
        clearButtons.forEach((button) => {
            button.addEventListener("click", () => this.clearNativeFilters())
        })
    }

    clearNativeFilters() {
        const form = this.formTarget
        form.reset()

        // Clear all input types except specific fields and user preference filters
        form.querySelectorAll('input').forEach(input => {
            const fieldName = input.name.replace(/^product_filter\[|\]$/g, '')
            if (!['page', 'only_count', 'order_by', 'filter_type', 'exclude_products_with_disliked_ingredients', 'apply_user_product_preferences'].includes(fieldName)) {
                if (input.type === 'checkbox' || input.type === 'radio') {
                    input.checked = false
                } else {
                    input.value = ''
                }
            }
        })

        // Clear selects except specific fields
        form.querySelectorAll('select').forEach(select => {
            const fieldName = select.name.replace(/^product_filter\[|\]$/g, '')
            if (!['page', 'only_count', 'order_by'].includes(fieldName)) {
                select.selectedIndex = 0
                if (select.tomselect) {
                    select.tomselect.clear()
                }
            }
        })

        // Dispatch clear-filters event to reset price range
        document.dispatchEvent(new CustomEvent('clear-filters'))

        this.debouncedSubmitForm(false)
    }

    handleClearFiltersButtonClick(event) {
        const contentDiv = document.getElementById("all_product_filters")
        if (!contentDiv) return

        const inputs = contentDiv.querySelectorAll("input")
        inputs.forEach((input) => {
            const fieldName = input.name.replace(/^product_filter\[|\]$/g, '')
            if (!['order_by', 'exclude_products_with_disliked_ingredients', 'apply_user_product_preferences'].includes(fieldName)) {
                if (input.type === "checkbox" || input.type === "radio") {
                    input.checked = false
                } else {
                    input.value = ""
                }
            }
        })

        const selects = contentDiv.querySelectorAll("select")
        selects.forEach((select) => {
            const fieldName = select.name.replace(/^product_filter\[|\]$/g, '')
            if (!['order_by'].includes(fieldName)) {
                select.value = ""
                // Check if the select is a TomSelect instance and clear it
                if (select.tomselect) {
                    select.tomselect.clear()
                }
            }
        })

        // Dispatch clear-filters event to reset price range
        document.dispatchEvent(new CustomEvent('clear-filters'))

        this.debouncedSubmitForm(false)
    }

    addTurboFrameLoadListener() {
        document.addEventListener("turbo:frame-load", this.boundHandleTurboFrameLoad)
    }

    handleTurboFrameLoad() {
        this.refreshEventListeners()
    }

    refreshEventListeners() {
        this.removeExistingEventListeners();
        this.addRemoveBrandFilterListener()
        this.addClearFiltersButtonListener()
        this.addAppClearFiltersButtonListener()
        this.attachRadioButtonListeners()
        this.attachInputChangeListeners()

        // Update the selector for the "See products" buttons
        this.showProductsButtonTargets.forEach(button => {
            button.addEventListener('click', this.handleShowProductsClick.bind(this))
        })

        // Reset the form's turbo frame to results_count
        this.resetFormTurboFrame()

        this.refreshAppliedFilters()
    }

    removeExistingEventListeners() {
        // Remove existing radio button listeners
        this.formTarget.querySelectorAll('input[type="radio"]:not([name="product_filter[order_by]"]):not(.ingredient-match-input)').forEach(radio => {
            // Create a new clone of the element to remove all event listeners
            const newRadio = radio.cloneNode(true);
            // Copy over the current checked state and lastChecked attribute
            newRadio.checked = radio.checked;
            newRadio.dataset.lastChecked = radio.dataset.lastChecked;
            // Replace the old element with the new one
            radio.parentNode.replaceChild(newRadio, radio);
        });

        // Remove existing input listeners, excluding TomSelect elements
        const inputElements = this.formTarget.getElementsByTagName("input");
        Array.from(inputElements).forEach(input => {
            // Skip if the input is part of a TomSelect component
            if (input.closest('.ts-wrapper')) {
                return;
            }

            const newInput = input.cloneNode(true);
            newInput.checked = input.checked;
            input.parentNode.replaceChild(newInput, input);
        });
    }

    resetFormTurboFrame() {
        // Reset the form's turbo frame to results_count so that the results count is updated by default on form submit
        this.formTarget.setAttribute('data-turbo-frame', 'results_count')
    }

    setupIngredientSelectors() {
        if (this.hasIngredientSelectTarget) {
            this.ingredientSelector = new ProductFilterIngredientSelector(this.ingredientSelectTarget)
        }

        if (this.hasExcludedIngredientSelectTarget) {
            this.excludedIngredientSelector = new ProductFilterIngredientSelector(this.excludedIngredientSelectTarget)
        }
    }

    addRemoveBrandFilterListener() {
        const removeBrandFilterButton = document.getElementById("remove-brand-filter-button")

        if (removeBrandFilterButton) {
            removeBrandFilterButton.addEventListener("click", () => {
                this.hiddenBrandInputTarget.value = ""
                this.debouncedSubmitForm()
            })
        }
    }

    handleInputChange(event, filterCollections) {
        // A full "See products" submission temporarily targets the products
        // frame and removes only_count. Restore the live-count submission state
        // before handling another filter change so a quick follow-up change does
        // not send a count-only response to the products frame.
        this.resetFormTurboFrame()
        this.addOnlyCountParam()

        const {
            categoryInputs,
            goodForInputs,
            ingredientCountInputs,
            spfLevelInputs,
            uvFilterInputs
        } = filterCollections

        // Several brand countries can be active at once, so the quick picks do
        // not clear each other - they only claim their code back from the
        // dropdown.
        if (event.target.classList.contains("country-input")) {
            this.clearBrandFilter()
            if (event.target.checked && this.hasCountrySelectTarget) this.deselectCountryOption(event.target.value)
        }

        if (event.target.classList.contains("brand-region-input")) this.clearBrandFilter()

        if (event.target.classList.contains("category-input")) {
            // For category inputs, we need to ensure they stay checked
            // This is important for the Alpine.js integration
            if (event.target.checked) {
                this.unCheckInputsExcludingTarget(categoryInputs, event.target)
            }
        }

        // Skin goals match on a product's primary purposes, so only one goal
        // can be selected at a time.
        if (event.target.classList.contains("good-for-input")) {
            this.unCheckInputsExcludingTarget(goodForInputs, event.target)
        }

        if (event.target.classList.contains("ingredient-count-input")) {
            this.unCheckInputsExcludingTarget(ingredientCountInputs, event.target)
        }

        if (event.target.classList.contains("spf-level-input")) {
            this.unCheckInputsExcludingTarget(spfLevelInputs, event.target)
        }

        if (event.target.classList.contains("uv-filter-input")) {
            this.unCheckInputsExcludingTarget(uvFilterInputs, event.target)
        }

        this.debouncedSubmitForm()
    }

    unCheckInputsExcludingTarget(inputElements, eventTarget) {
        inputElements.forEach((inputElement) => {
            if (inputElement !== eventTarget) {
                inputElement.checked = false
            }
        })
    }

    filterInputsByClassName(inputElements, className) {
        return Array.from(inputElements).filter((element) =>
            element.classList.contains(className)
        )
    }

    submitFormImpl() {
        // Pre-submit tasks
        this.updateShowProductsIndicator()
        this.disableSelectsIfNotSelected()

        this.formTarget.requestSubmit()

        // Post-submit tasks
        this.enableSelects()
        this.addOnlyCountParam()
    }

    // So that we submit the form with the only_count param by default
    // When doing a full submit this param is removed in handleShowProductsClick
    addOnlyCountParam() {
        if (!document.getElementById('product_filter_only_count')) {
            const newOnlyCountInput = document.createElement('input')
            newOnlyCountInput.type = 'hidden'
            newOnlyCountInput.id = 'product_filter_only_count'
            newOnlyCountInput.name = 'product_filter[only_count]'
            newOnlyCountInput.value = '1'
            this.formTarget.appendChild(newOnlyCountInput)
        }
    }

    // So we don't send its value instead of the selected radio button
    disableSelectsIfNotSelected() {
        if (this.hasCountrySelectTarget && !this.hasSelectedValue(this.countrySelectTarget)) {
            this.countrySelectTarget.disabled = true
        }

        if (this.hasVendorSelectTarget && !this.hasSelectedValue(this.vendorSelectTarget)) {
            this.vendorSelectTarget.disabled = true
        }
    }

    // A blank "Select a ..." option is a prompt, not a value, so a select holding
    // only that one counts as empty and is left out of the submission.
    hasSelectedValue(select) {
        return Array.from(select.selectedOptions).some(option => option.value !== "")
    }

    enableSelects() {
        if (this.hasCountrySelectTarget) {
            this.countrySelectTarget.disabled = false
        }
        if (this.hasVendorSelectTarget) {
            this.vendorSelectTarget.disabled = false
        }
    }

    updateShowProductsIndicator() {
        this.showProductsCountTargets.forEach((element) =>
            element.classList.add("hidden")
        )
        this.showProductsLoadingTargets.forEach((element) =>
            element.classList.remove("hidden")
        )
        this.nativeActionTargets.forEach((element) => {
            element.setAttribute("data-bridge--single-action-loading-value", "true")
        })
    }

    updateNativeClearVisibility(visible) {
        this.nativeClearTargets.forEach((element) => {
            element.setAttribute("data-bridge--button-visible-value", visible.toString())
        })
    }

    finishCountSubmission(event) {
        if (event.detail.success) return

        this.nativeActionTargets.forEach((element) => {
            element.setAttribute("data-bridge--single-action-loading-value", "false")
        })
    }

    handleShowProductsClick(event) {
        event.preventDefault()

        // Remove the only_count param
        let onlyCountInput = document.getElementById('product_filter_only_count');

        if (onlyCountInput) {
            onlyCountInput.remove();
        }

        if (this.fullPageSubmitValue) {
            this.formTarget.action = this.productsPathValue
            this.formTarget.setAttribute('data-turbo-frame', '_top')
        } else {
            this.formTarget.setAttribute('data-turbo-frame', 'products')
        }

        // Use the debounced submit form
        this.debouncedSubmitForm()
    }

    // Clean up when the controller is disconnected
    disconnect() {
        document.removeEventListener('price-range:change', this.boundHandlePriceRangeChange)
        document.removeEventListener('turbo:frame-load', this.boundHandleTurboFrameLoad)

        // Destroy TomSelect instances to prevent memory leaks
        if (this.ingredientSelector) {
            this.ingredientSelector.destroy()
        }

        if (this.excludedIngredientSelector) {
            this.excludedIngredientSelector.destroy()
        }
    }

    // Applied-filter chips and per-section summaries in the combined drawer.
    // Both are rebuilt from the live form state on every change, so they can
    // never drift from the inputs the way a server-rendered snapshot could.
    refreshAppliedFilters() {
        if (!this.hasFormTarget) return

        const entries = this.collectActiveFilterEntries()
        this.renderAppliedChips(entries)
        this.updateSectionSummaries(entries)
    }

    collectActiveFilterEntries() {
        const entries = []

        this.formTarget.querySelectorAll("input.filter-input").forEach(input => {
            if (!input.checked) return
            if (input.classList.contains("ingredient-match-input")) return
            // A blank radio value is a section's "Any"/off state, not a filter.
            if (input.type === "radio" && input.value === "") return
            if (input.name === "product_filter[order_by]") return

            const label = input.dataset.chipLabel || this.chipLabelFor(input)
            if (!label) return

            entries.push({ kind: "input", id: input.id, label, section: this.sectionNameFor(input) })
        })

        const selectEntries = [
            { targetName: "ingredientSelect", present: this.hasIngredientSelectTarget, prefix: "Includes " },
            { targetName: "excludedIngredientSelect", present: this.hasExcludedIngredientSelectTarget, prefix: "No " },
            { targetName: "vendorSelect", present: this.hasVendorSelectTarget, prefix: "" },
            { targetName: "countrySelect", present: this.hasCountrySelectTarget, prefix: "" }
        ]
        selectEntries.forEach(({ targetName, present, prefix }) => {
            if (!present) return
            const select = this[`${targetName}Target`]
            Array.from(select.selectedOptions).forEach(option => {
                if (option.value === "") return
                const label = `${prefix}${option.textContent.trim()}`.trim()
                if (!label) return
                entries.push({ kind: "select", targetName, value: option.value, label, section: this.sectionNameFor(select) })
            })
        })

        const priceEntry = this.activePriceEntry()
        if (priceEntry) entries.push(priceEntry)

        return entries
    }

    chipLabelFor(input) {
        const label = input.closest("label")
        if (!label) return null

        return label.textContent.replace(/\s+/g, " ").trim()
    }

    sectionNameFor(element) {
        return element.closest("[data-product-filter-section]")?.dataset?.productFilterSection
    }

    activePriceEntry() {
        if (!this.hasPriceMinInputTarget || !this.hasPriceMaxInputTarget) return null

        const min = this.priceMinInputTarget
        const max = this.priceMaxInputTarget
        const minActive = !min.disabled && min.value !== "" && min.value !== "0"
        const maxActive = !max.disabled && max.value !== "" && max.value !== "100"
        if (!minActive && !maxActive) return null

        // The slider's dollar labels are the readable form of the raw 0-100
        // range values, so the chip borrows them instead of the input values.
        const minLabel = this.element.querySelector('[data-price-range-target="minLabel"]')?.textContent?.trim()
        const maxLabel = this.element.querySelector('[data-price-range-target="maxLabel"]')?.textContent?.trim()
        const label = minLabel && maxLabel ? `${minLabel} to ${maxLabel}` : "Price range"

        return { kind: "price", label, section: this.sectionNameFor(min) }
    }

    renderAppliedChips(entries) {
        if (!this.hasAppliedChipsTarget) return

        const container = this.appliedChipsTarget
        container.innerHTML = ""
        container.classList.toggle("invisible", entries.length === 0)

        entries.forEach(entry => {
            const chip = document.createElement("button")
            chip.type = "button"
            chip.className = "trigger-native-haptic-light flex flex-none items-center gap-1.5 rounded-full border border-warm-gray-200 bg-warm-gray-50 py-1.5 pl-3.5 pr-2.5 text-[13px] font-medium tracking-tight text-warm-gray-800 lg:hover:bg-warm-gray-100"
            chip.setAttribute("aria-label", `Remove ${entry.label} filter`)

            const text = document.createElement("span")
            text.className = "max-w-44 truncate"
            text.textContent = entry.label
            chip.appendChild(text)

            const icon = document.createElement("span")
            icon.className = "flex-none text-warm-gray-500"
            icon.innerHTML = '<svg viewBox="0 0 20 20" fill="currentColor" class="size-3.5" aria-hidden="true"><path fill-rule="evenodd" d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" clip-rule="evenodd"/></svg>'
            chip.appendChild(icon)

            chip.addEventListener("click", () => this.removeAppliedFilter(entry))
            container.appendChild(chip)
        })
    }

    updateSectionSummaries(entries) {
        const labelsBySection = {}
        entries.forEach(entry => {
            if (!entry.section) return
            labelsBySection[entry.section] = labelsBySection[entry.section] || []
            labelsBySection[entry.section].push(entry.label)
        })

        this.element.querySelectorAll("[data-product-filter-section]").forEach(section => {
            const summary = section.querySelector("[data-product-filter-summary]")
            if (!summary) return

            const labels = labelsBySection[section.dataset.productFilterSection] || []
            // One filter reads as itself; several would truncate into noise, so
            // the header falls back to a count.
            summary.textContent = labels.length > 1 ? `${labels.length} applied` : labels.join("")
            summary.classList.toggle("hidden", labels.length === 0)
            section.querySelector("[data-product-filter-active-dot]")?.classList?.toggle("hidden", labels.length === 0)
        })
    }

    removeAppliedFilter(entry) {
        if (entry.kind === "input") {
            const input = entry.id ? document.getElementById(entry.id) : null
            if (!input) return

            input.checked = false
            if (input.type === "radio") input.dataset.lastChecked = "false"
            // The change listeners attached to the input handle the submit.
            input.dispatchEvent(new Event("change", { bubbles: true }))
        } else if (entry.kind === "select") {
            this.removeSelectValue(entry.targetName, entry.value)
        } else if (entry.kind === "price") {
            this.clearPriceRange()
        }

        this.refreshAppliedFilters()
    }

    removeSelectValue(targetName, value) {
        const select = this[`${targetName}Target`]
        if (!select) return

        if (select.tomselect) {
            if (select.multiple) {
                select.tomselect.removeItem(value)
            } else {
                select.tomselect.clear()
            }
        } else if (select.multiple) {
            Array.from(select.options).forEach(option => {
                if (option.value === value) option.selected = false
            })
        } else {
            select.value = ""
        }

        select.dispatchEvent(new Event("change", { bubbles: true }))
    }

    clearPriceRange() {
        this.priceMinInputTarget.value = ""
        this.priceMinInputTarget.disabled = true
        this.priceMaxInputTarget.value = ""
        this.priceMaxInputTarget.disabled = true

        // Resets the slider UI; only the price-range controller listens.
        document.dispatchEvent(new CustomEvent("clear-filters"))
        this.debouncedSubmitForm()
    }

    // Helper method to submit the form without interference from other handlers
    submitWithoutInterference(action) {
        // Temporarily remove all change event listeners from the form
        const oldSubmit = this.debouncedSubmitForm
        this.debouncedSubmitForm = () => { }

        // Perform the action
        action()

        // Restore the submit function and call it
        this.debouncedSubmitForm = oldSubmit
        this.debouncedSubmitForm()
    }
}

// Keep the String prototype extension
if (!String.prototype.toProperCase) {
    String.prototype.toProperCase = function () {
        return this.replace(/\w\S*/g, function (txt) {
            return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
        })
    }
}
